
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSession } from '@supabase/auth-helpers-react';
import { toast } from './use-toast';
import { useQueryClient, useQuery } from '@tanstack/react-query';

export interface CartItemProduct {
  id: string;
  name: string;
  price: number;
  image_url?: string;
  stock_count: number;
  brand?: string;
  category?: string;
  is_preorder?: boolean;
  is_on_sale?: boolean;
  sale_price?: number | null;
  sale_start_date?: string | null;
  sale_end_date?: string | null;
}

export interface CartItem {
  id?: string;
  product: CartItemProduct;
  quantity: number;
}

// Define consistent query key
const getCartQueryKey = (userId?: string) => ['cart-items', userId || 'guest'];

export const useCart = () => {
  const [isLoading, setIsLoading] = useState(false);
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;

  const { data: items = [], refetch, isError } = useQuery({
    queryKey: getCartQueryKey(userId),
    queryFn: async () => {
      console.log('useCart: Fetching cart items for user:', userId || 'guest');
      
      if (userId) {
        // Logged-in user: fetch from database
        const { data, error } = await supabase
          .from('cart_items')
          .select(`
            id,
            quantity,
            products (
              id,
              name,
              price,
              image_url,
              stock_count,
              brand,
              category,
              is_preorder,
              is_on_sale,
              sale_price,
              sale_start_date,
              sale_end_date
            )
          `)
          .eq('user_id', userId);

        if (error) {
          console.error('Error fetching cart items:', error);
          throw error;
        }
        
        // Transform to match CartItem interface
        const transformedItems = (data || [])
          .filter(item => item.products) // Filter out items with null products
          .map(item => ({
            id: item.id,
            product: {
              id: item.products.id,
              name: item.products.name,
              price: item.products.price,
              image_url: item.products.image_url,
              stock_count: item.products.stock_count,
              brand: item.products.brand,
              category: item.products.category,
              is_preorder: item.products.is_preorder,
              is_on_sale: item.products.is_on_sale,
              sale_price: item.products.sale_price,
              sale_start_date: item.products.sale_start_date,
              sale_end_date: item.products.sale_end_date
            },
            quantity: item.quantity
          })) as CartItem[];
        
        console.log('useCart: Loaded database cart items:', transformedItems.length);
        return transformedItems;
      } else {
        // Guest user: load from localStorage
        const savedCart = localStorage.getItem('guestCart');
        if (savedCart) {
          try {
            const parsedCart = JSON.parse(savedCart) as CartItem[];
            console.log('useCart: Loaded guest cart from localStorage:', parsedCart.length);
            return parsedCart;
          } catch (error) {
            console.error('Error parsing guest cart:', error);
            localStorage.removeItem('guestCart');
            return [];
          }
        }
        return [];
      }
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // Update localStorage for guest users when items change
  useEffect(() => {
    if (!userId && items.length >= 0) {
      if (items.length === 0) {
        localStorage.removeItem('guestCart');
        console.log('useCart: Removed empty guest cart from localStorage');
      } else {
        localStorage.setItem('guestCart', JSON.stringify(items));
        console.log('useCart: Saved guest cart to localStorage:', items.length);
      }
    }
  }, [items, userId]);

  const addItem = async ({ product, quantity }: CartItem) => {
    setIsLoading(true);
    console.log('useCart: Adding item to cart:', product.name, 'quantity:', quantity);
    
    try {
      const existingItemIndex = items.findIndex(
        item => item.product.id === product.id
      );

      let updatedItems: CartItem[];

      if (userId) {
        // Logged-in user: save to database
        if (existingItemIndex > -1) {
          // Update quantity if item already exists
          const newQuantity = items[existingItemIndex].quantity + quantity;
          const { error } = await supabase
            .from('cart_items')
            .update({ quantity: newQuantity })
            .eq('id', items[existingItemIndex].id)
            .eq('user_id', userId);

          if (error) throw error;

          updatedItems = [...items];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: newQuantity
          };
        } else {
          // Add new item
          const { data, error } = await supabase
            .from('cart_items')
            .insert({
              user_id: userId,
              product_id: product.id,
              quantity,
            })
            .select('id')
            .single();

          if (error) throw error;

          updatedItems = [...items, { id: data.id, product, quantity }];
        }
      } else {
        // Guest user: update localStorage
        if (existingItemIndex > -1) {
          updatedItems = [...items];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: updatedItems[existingItemIndex].quantity + quantity
          };
        } else {
          updatedItems = [...items, { 
            id: `guest-${product.id}`, 
            product, 
            quantity 
          }];
        }
      }

      // Update cache immediately
      queryClient.setQueryData(getCartQueryKey(userId), updatedItems);
      
      console.log('useCart: Cart updated successfully with', updatedItems.length, 'items');
      
      toast({
        title: "Added to cart",
        description: `${product.name} has been added to your cart`,
      });
      
    } catch (error) {
      console.error('Error adding item to cart:', error);
      toast({
        title: "Error",
        description: "Failed to add item to cart",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const removeItem = async (productId: string) => {
    console.log('useCart: Removing item from cart:', productId);
    
    try {
      if (userId) {
        // Logged-in user: remove from database
        const itemToRemove = items.find(item => item.product.id === productId);
        if (!itemToRemove?.id) return;

        const { error } = await supabase
          .from('cart_items')
          .delete()
          .eq('id', itemToRemove.id)
          .eq('user_id', userId);

        if (error) throw error;
      }

      // Update local state for both logged-in and guest users
      const updatedItems = items.filter(item => item.product.id !== productId);
      queryClient.setQueryData(getCartQueryKey(userId), updatedItems);
      
      console.log('useCart: Removed item, updated with', updatedItems.length, 'items');
      
    } catch (error) {
      console.error('Error removing item from cart:', error);
      toast({
        title: "Error",
        description: "Failed to remove item from cart",
        variant: "destructive",
      });
    }
  };

  const updateQuantity = async (productId: string, quantity: number) => {
    try {
      if (userId) {
        // Logged-in user: update in database
        const itemToUpdate = items.find(item => item.product.id === productId);
        if (!itemToUpdate?.id) return;

        const { error } = await supabase
          .from('cart_items')
          .update({ quantity })
          .eq('id', itemToUpdate.id)
          .eq('user_id', userId);

        if (error) throw error;
      }

      // Update local state for both logged-in and guest users
      const updatedItems = items.map(item => 
        item.product.id === productId ? { ...item, quantity } : item
      );
      
      queryClient.setQueryData(getCartQueryKey(userId), updatedItems);
      
    } catch (error) {
      console.error('Error updating quantity:', error);
      toast({
        title: "Error",
        description: "Failed to update quantity",
        variant: "destructive",
      });
    }
  };

  const clearCart = async () => {
    try {
      if (userId) {
        // Logged-in user: clear from database
        const { error } = await supabase
          .from('cart_items')
          .delete()
          .eq('user_id', userId);

        if (error) throw error;
      }

      // Clear cache and localStorage
      queryClient.setQueryData(getCartQueryKey(userId), []);
      localStorage.removeItem('guestCart');
      
      console.log('useCart: Cart cleared successfully');
    } catch (error) {
      console.error('Error clearing cart:', error);
      toast({
        title: "Error",
        description: "Failed to clear cart",
        variant: "destructive",
      });
    }
  };

  const getCartTotal = () => {
    return items.reduce((total, item) => {
      // Use sale price if item is on sale and sale price exists
      const effectivePrice = item.product.is_on_sale && item.product.sale_price 
        ? item.product.sale_price 
        : item.product.price;
      return total + effectivePrice * item.quantity;
    }, 0);
  };

  return {
    items,
    isLoading,
    isError,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getCartTotal,
    refetch
  };
};
